/**
 * Redux async actions (src/store/actions/bookingActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

// Keep rejected booking actions machine-readable without forwarding backend/provider text.
export const normalizeBookingError = (error, fallbackMessage) => {
  const status = Number(error?.response?.status);
  const knownStatus = [401, 403, 404, 409].includes(status) ? status : null;
  const details = {
    401: { code: "AUTHENTICATION_REQUIRED", message: "Your session has expired. Please sign in again." },
    403: { code: "FORBIDDEN", message: "You do not have permission to manage this booking." },
    404: { code: "NOT_FOUND", message: "The booking could not be found. Refresh and try again." },
    409: { code: "BOOKING_CONFLICT", message: "The booking changed or the selected slot is no longer available." },
  }[knownStatus] || { code: "BOOKING_REQUEST_FAILED", message: fallbackMessage };

  const conflictCode = error?.response?.data?.code;
  const approvedConflictCodes = ['RESCHEDULE_CUTOFF', 'BOOKING_ENTITLEMENT_CLOSED',
    'SUBSCRIPTION_PERIOD_EXCEEDED', 'SUBSCRIPTION_NOT_ACTIVE', 'SLOT_UNAVAILABLE'];
  return { status: knownStatus, ...details,
    ...(knownStatus === 409 && typeof conflictCode === 'string' && approvedConflictCodes.includes(conflictCode)
      ? { code: conflictCode } : {}) };
};

// Keep existing actions that still have endpoints
/**
 * Async thunk: GetBookingsByStudentID.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetBookingsByStudentID = createAsyncThunk(
  "bookings/GetBookingsByStudentID",
  async (id) => {
    try {
      const response = await axios.get(`GetBookingsByStudentID/${id}`);
      return response.data.Bookings;
    } catch (error) {
      console.error("Error fetching meetings:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: GetBookingsByTeacherID.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetBookingsByTeacherID = createAsyncThunk(
  "bookings/GetBookingsByTeacherID",
  async (TeacherID, { rejectWithValue }) => {
    try {
      const response = await axios.get(`GetBookingsByTeacherID/${TeacherID}`);
      return Array.isArray(response.data?.Bookings) ? response.data.Bookings : [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: error.message || 'Failed to fetch teacher bookings',
        }
      );
    }
  }
);

/**
 * Async thunk: GetExistingTeacherAvailability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetExistingTeacherAvailability = createAsyncThunk(
  "bookings/GetExistingTeacherAvailability",
  async (BookingID, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/GetExistingTeacher_Availability/${BookingID}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Optimized month-wise calendar data action
/**
 * Async thunk: GetCalendarDataByMonth.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetCalendarDataByMonth = createAsyncThunk(
  "bookings/GetCalendarDataByMonth",
  async ({ studentId, teacherId, year, month, timezone = 'UTC' }, { rejectWithValue }) => {
    try {
     
      
      // Build URL based on whether studentId is provided (without timezone in path)
      let url;
      if (studentId && studentId.trim() && studentId !== 'undefined' && studentId !== 'null') {
        url = `/calendar-data/${studentId}/${teacherId}/${year}/${month}`;
      } else {
        url = `/calendar-data/${teacherId}/${year}/${month}`;
      }
      
      
      const response = await axios.get(url, { params: { timezone } });
     
      return response.data.data;
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      return rejectWithValue(error.response?.data || 'Failed to fetch calendar data');
    }
  }
);

/**
 * Async thunk: GetExistingTeacherAvailability__oF_Package.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetExistingTeacherAvailability__oF_Package = createAsyncThunk(
  "bookings/GetExistingTeacherAvailability__oF_Package",
  async (Package_ID) => {
    try {
      const response = await axios.get(
        `GetExistingTeacherAvailability__oF_Package/${Package_ID}`
      );
      return response;
    } catch (error) {
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: SearchBookingbyStudentUsername.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const SearchBookingbyStudentUsername = createAsyncThunk(
  "bookings/SearchBookingbyStudentUsername",
  async (input) => {
    try {
      const response = await axios.post(
        `SearchBookingbyStudentUsername/${input}`
      );
      return response;
    } catch (error) {
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: UpdatetheBookingStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const UpdatetheBookingStatus = createAsyncThunk(
  "bookings/UpdatetheBookingStatus",
  async (bookingId) => {
    try {
      const response = await axios.get(`UpdatetheBookingStatus/${bookingId}`);
      return response;
    } catch (error) {
      
      return error.response.data.message;
    }
  }
);

/**
 * Async thunk: AddOneHourToPackage.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const AddOneHourToPackage = createAsyncThunk(
  "wallet/AddOneHourToPackage",
  async ({ studentId, packageId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`wallet/addOneHour`, { studentId, packageId });
      return response.data;
    } catch (error) {
      console.error("Error adding one hour:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while adding one hour');
    }
  }
);

// New actions for the new booking endpoints
/**
 * Async thunk: Add_booking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Add_booking = createAsyncThunk(
  "bookings/createBooking",
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`v1/bookings`, bookingData);
      return response.data;
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while creating booking'
      );
      console.error("Error creating booking:", safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

export const createBookingsWithWallet = createAsyncThunk(
  "bookings/createBookingsWithWallet",
  async (bookingData, { rejectWithValue }) => {
    try {
      const payload = Array.isArray(bookingData) ? bookingData : bookingData?.bookings || bookingData;
      const response = await axios.post(`v1/bookings/wallet`, payload);
      return response.data;
    } catch (error) {
      console.error("Error creating wallet-backed bookings:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while creating wallet-backed bookings');
    }
  }
);


/**
 * Async thunk: createTrialBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createTrialBooking = createAsyncThunk(
  "bookings/createTrialBooking",
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`v1/bookings/trial`, bookingData);
      return response.data;
    } catch (error) {
      console.error("Error creating trial booking:", error.response?.data || error.message);
      if (error.response?.data) {
        return rejectWithValue({ ...error.response.data, status: error.response.status });
      }
      return rejectWithValue({ message: error.message });
    }
  }
);

/**
 * Async thunk: fetchAllbookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchAllbookings = createAsyncThunk(
  "bookings/fetchBookings",
  async (params = {}, { rejectWithValue }) => {
    try {
      const requestedPage = Number.isInteger(Number(params.page)) && Number(params.page) > 0
        ? Number(params.page)
        : 1;
      const requestedLimit = Number.isInteger(Number(params.limit)) && Number(params.limit) > 0
        ? Number(params.limit)
        : 10000;
      const fetchAllPages = params.fetchAllPages === true;
      // The API intentionally caps one page at 100 records. Admin screens can
      // still request the complete list, but must do so page by page.
      const pageLimit = Math.min(requestedLimit, 100);

      const queryParams = {
        // Optional teacher and student IDs - can pass either, both, or none
        teacherId: params.teacherId,
        studentId: params.studentId,
        includeChildren: params.includeChildren,
        
        // Pagination parameters with defaults
        page: requestedPage,
        limit: fetchAllPages ? pageLimit : requestedLimit,
        
        // Optional filtering parameters
        packageId: params.packageId,
        status: params.status,
        startDate: params.startDate,
        endDate: params.endDate,
        isTrialBooking: params.isTrialBooking,
        paymentStatus: params.paymentStatus,
        bookingCode: params.bookingCode,
        isPast: params.isPast,
        archiveScope: params.archiveScope,
        search: params.search,
        timeOfDay: params.timeOfDay,
        timezone: params.timezone,
        sortBy: params.sortBy || 'startTime',
        sortOrder: params.sortOrder || 'asc'
      };

      const fetchPage = async (page) => {
        const response = await axios.get(`v1/bookings`, {
          params: { ...queryParams, page },
        });
        return normalizeBookingListPayload(response.data);
      };

      const firstPayload = await fetchPage(requestedPage);
      if (!fetchAllPages || !Array.isArray(firstPayload?.bookings)) {
        return firstPayload;
      }

      // When the admin screen asks for the complete collection, only the
      // canonical paginated response can prove that the collection is
      // complete. Legacy envelopes without pagination remain usable as a
      // single-page response, but are never marked as a complete snapshot.
      const firstPagination = getCompleteBookingPagination(
        firstPayload,
        requestedPage,
        pageLimit
      );
      if (!firstPagination) {
        if (!firstPayload.pagination) {
          return firstPayload;
        }
        return rejectWithValue({
          code: 'INCOMPLETE_BOOKING_LIST_RESPONSE',
          message: 'Nie udało się potwierdzić pełnej listy rezerwacji.',
        });
      }

      const totalPages = firstPagination.totalPages;
      if (totalPages === 0) {
        return {
          ...firstPayload,
          pagination: {
            ...firstPayload.pagination,
            loadedPages: 0,
            isComplete: true,
          },
        };
      }

      const bookingIds = new Set();
      const rememberBookings = (pagePayload) => {
        for (const booking of pagePayload.bookings) {
          const bookingId = String(booking?._id || booking?.id || '');
          if (!bookingId || bookingIds.has(bookingId)) {
            return false;
          }
          bookingIds.add(bookingId);
        }
        return true;
      };

      if (!rememberBookings(firstPayload)) {
        return rejectWithValue({
          code: 'INCOMPLETE_BOOKING_LIST_RESPONSE',
          message: 'Lista rezerwacji zawiera zduplikowane lub niepełne rekordy.',
        });
      }

      if (totalPages <= requestedPage) {
        if (firstPayload.bookings.length !== firstPagination.totalItems) {
          return rejectWithValue({
            code: 'INCOMPLETE_BOOKING_LIST_RESPONSE',
            message: 'Liczba rezerwacji nie zgadza się z metadanymi API.',
          });
        }
        return {
          ...firstPayload,
          pagination: {
            ...firstPayload.pagination,
            loadedPages: 1,
            isComplete: true,
          },
        };
      }

      const bookings = [...firstPayload.bookings];
      for (let page = requestedPage + 1; page <= totalPages; page += 1) {
        const pagePayload = await fetchPage(page);
        const pagination = getCompleteBookingPagination(pagePayload, page, pageLimit);
        if (
          !pagination
          || pagination.totalPages !== firstPagination.totalPages
          || pagination.totalItems !== firstPagination.totalItems
          || !rememberBookings(pagePayload)
        ) {
          return rejectWithValue({
            code: 'INCOMPLETE_BOOKING_LIST_RESPONSE',
            message: 'Nie udało się potwierdzić pełnej listy rezerwacji.',
          });
        }
        bookings.push(...pagePayload.bookings);
      }

      if (bookings.length !== firstPagination.totalItems) {
        return rejectWithValue({
          code: 'INCOMPLETE_BOOKING_LIST_RESPONSE',
          message: 'Liczba rezerwacji nie zgadza się z metadanymi API.',
        });
      }

      return {
        ...firstPayload,
        bookings,
        pagination: {
          ...firstPayload.pagination,
          currentPage: requestedPage,
          totalPages,
          totalItems: firstPagination.totalItems,
          itemsPerPage: firstPagination.itemsPerPage,
          loadedPages: totalPages - requestedPage + 1,
          isComplete: true,
        },
      };
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while fetching bookings'
      );
      console.error("Error fetching bookings:", safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

/**
 * Normalize legacy booking-list envelopes at the API boundary. Older
 * deployments returned `data`, `Bookings`, or `data.Bookings`; treating those
 * responses as a successful empty list made the admin screen look blank even
 * though the database query had returned records.
 */
export const normalizeBookingListPayload = (payload) => {
  if (Array.isArray(payload)) {
    return { success: true, bookings: payload };
  }

  if (!payload || typeof payload !== 'object' || Array.isArray(payload.bookings)) {
    return payload;
  }

  const nestedData = payload.data && !Array.isArray(payload.data) ? payload.data : null;
  const bookingCandidates = [
    payload.Bookings,
    Array.isArray(payload.data) ? payload.data : null,
    nestedData?.bookings,
    nestedData?.Bookings,
    payload.Allbookinglist?.bookings,
    payload.Allbookinglist?.data,
  ];
  const bookings = bookingCandidates.find(Array.isArray);

  if (!bookings) {
    return payload;
  }

  return {
    ...payload,
    success: payload.success ?? true,
    bookings,
    pagination: payload.pagination ?? nestedData?.pagination ?? null,
    summary: payload.summary ?? nestedData?.summary,
  };
};

export const INVALID_COMPLETE_BOOKING_LIST_RESPONSE = 'Received an incomplete bookings response';

export const isValidBookingListResponse = (payload) => (
  payload?.success === true && Array.isArray(payload.bookings)
);

const getCompleteBookingPagination = (payload, expectedPage, requestedLimit) => {
  const currentPage = Number(payload?.pagination?.currentPage);
  const totalPages = Number(payload?.pagination?.totalPages);
  const totalItems = Number(payload?.pagination?.totalItems);
  const itemsPerPage = Number(payload?.pagination?.itemsPerPage);

  if (
    !Number.isSafeInteger(currentPage)
    || !Number.isSafeInteger(totalPages)
    || !Number.isSafeInteger(totalItems)
    || !Number.isSafeInteger(itemsPerPage)
    || currentPage !== expectedPage
    || totalPages < 0
    || totalItems < 0
    || itemsPerPage !== requestedLimit
  ) {
    return null;
  }

  // An empty canonical response is represented as page 1 of zero pages.
  if (totalPages === 0) {
    return expectedPage === 1 && totalItems === 0 && payload.bookings.length === 0
      ? { currentPage, totalPages, totalItems, itemsPerPage }
      : null;
  }

  if (expectedPage > totalPages || totalPages !== Math.ceil(totalItems / itemsPerPage)) {
    return null;
  }

  return { currentPage, totalPages, totalItems, itemsPerPage };
};

/**
 * Fetch a teacher schedule as one confirmed snapshot.
 *
 * A regular booking query remains page scoped for list screens. Teacher
 * schedule consumers use this reader so a later-page failure cannot replace a
 * confirmed schedule with a partial collection.
 */
export const fetchCompleteBookings = createAsyncThunk(
  'bookings/fetchCompleteBookings',
  async (params = {}, { rejectWithValue, signal }) => {
    const teacherId = String(params.teacherId || '').trim();
    const requestedLimit = Number(params.limit);
    const limit = Number.isSafeInteger(requestedLimit) && requestedLimit > 0 && requestedLimit <= 100
      ? requestedLimit
      : 50;

    if (!teacherId) {
      return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
    }

    const buildQueryParams = (page) => ({
      teacherId,
      page,
      limit,
      sortBy: params.sortBy || 'startTime',
      sortOrder: params.sortOrder || 'asc',
    });

    try {
      const bookings = [];
      const bookingIds = new Set();
      const visitedPages = new Set();
      let expectedTotalPages = null;
      let expectedTotalItems = null;
      let firstPayload = null;
      let lastPagination = null;

      for (let page = 1; expectedTotalPages === null || page <= expectedTotalPages; page += 1) {
        if (signal?.aborted || visitedPages.has(page)) {
          return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
        }
        visitedPages.add(page);

        const response = await axios.get('v1/bookings', { params: buildQueryParams(page) });
        const payload = response?.data;
        if (!isValidBookingListResponse(payload)) {
          return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
        }

        const pagination = getCompleteBookingPagination(payload, page, limit);
        if (
          !pagination
          || (expectedTotalPages !== null && pagination.totalPages !== expectedTotalPages)
          || (expectedTotalItems !== null && pagination.totalItems !== expectedTotalItems)
        ) {
          return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
        }

        expectedTotalPages = pagination.totalPages;
        expectedTotalItems = pagination.totalItems;
        firstPayload = firstPayload || payload;
        lastPagination = payload.pagination;

        for (const booking of payload.bookings) {
          const bookingId = String(booking?._id || booking?.id || '');
          if (!bookingId || bookingIds.has(bookingId)) {
            return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
          }
          bookingIds.add(bookingId);
          bookings.push(booking);
        }

        if (bookings.length > expectedTotalItems) {
          return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
        }
      }

      if (!firstPayload || bookings.length !== expectedTotalItems) {
        return rejectWithValue(INVALID_COMPLETE_BOOKING_LIST_RESPONSE);
      }

      return {
        ...firstPayload,
        bookings,
        pagination: {
          ...lastPagination,
          currentPage: expectedTotalPages || 1,
          totalPages: expectedTotalPages,
          totalItems: expectedTotalItems,
          loadedPages: visitedPages.size,
          isComplete: true,
        },
      };
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while fetching bookings'
      );
      console.error('Error fetching complete bookings:', safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

/**
 * Async thunk: getBookingByCode.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getBookingByCode = createAsyncThunk(
  "bookings/getBookingByCode",
  async (bookingCode, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/bookings/code/${bookingCode}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching booking by code:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while fetching booking');
    }
  }
);

/**
 * Async thunk: fetchExistingBookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchExistingBookings = createAsyncThunk(
  "bookings/getUpcomingBookings",
  async ({ userType, userId, limit }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/bookings/upcoming/${userType}/${userId}`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching upcoming bookings:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while fetching upcoming bookings');
    }
  }
);

/**
 * Async thunk: getTeacherAvailability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherAvailability = createAsyncThunk(
  "bookings/getTeacherAvailability",
  async ({ teacherId, startDate, endDate, rangeOverride = false, overrideReason = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/bookings/availability/${teacherId}`, {
        params: { startDate, endDate, ...(rangeOverride ? { rangeOverride: true, overrideReason } : {}) }
      });
      return response.data;
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while fetching teacher availability'
      );
      console.error("Error fetching teacher availability:", safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

/**
 * Async thunk: Updatebooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Updatebooking = createAsyncThunk(
  "bookings/updateBooking",
  async (payload, { rejectWithValue }) => {
    try {
      // Handle both old and new parameter formats
      const id = payload.id || payload.BookingID;
      const data = payload.data || payload.updatedData;
      
      const response = await axios.put(`v1/bookings/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while updating booking');
    }
  }
);

/**
 * Async thunk: markAttendance.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const markAttendance = createAsyncThunk(
  "bookings/markAttendance",
  async ({ bookingId, attendanceData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/attendance`, attendanceData);
      return response.data;
    } catch (error) {
      console.error("Error marking attendance:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while marking attendance');
    }
  }
);

/**
 * Async thunk: cancelBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const cancelBooking = createAsyncThunk(
  "bookings/cancelBooking",
  async ({ bookingId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/cancel`, { reason });
      return response.data;
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while canceling booking'
      );
      console.error("Error canceling booking:", safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

/**
 * Async thunk: rescheduleBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const rescheduleBooking = createAsyncThunk(
  "bookings/rescheduleBooking",
  async ({ bookingId, startTime, endTime, reason, timezone }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `v1/bookings/${bookingId}/reschedule`,
        { startTime, endTime, reason, ...(timezone ? { timezone } : {}) }
      );
      return response.data;
    } catch (error) {
      const safeError = normalizeBookingError(
        error,
        'An error occurred while rescheduling booking'
      );
      console.error("Error rescheduling booking:", safeError.code, safeError.status);
      return rejectWithValue(safeError);
    }
  }
);

/**
 * Async thunk: completeBooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const completeBooking = createAsyncThunk(
  "bookings/completeBooking",
  async ({ bookingId, notes, recordingUrl }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `v1/bookings/${bookingId}/complete`, 
        { notes, recordingUrl }
      );
      return response.data;
    } catch (error) {
      console.error("Error completing booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while completing booking');
    }
  }
);

/**
 * Async thunk: Deletebooking.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Deletebooking = createAsyncThunk(
  "bookings/deleteBooking",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`v1/bookings/${id}`);
      return { id, response: response.data };
    } catch (error) {
      console.error("Error deleting booking:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while deleting booking');
    }
  }
);

/**
 * Async thunk: fetchBookingsExcludingStudent.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchBookingsExcludingStudent = createAsyncThunk(
  "bookings/fetchBookingsExcludeStudent",
  async ({ excludeStudentId, ...params }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/bookings/exclude-student/${excludeStudentId}`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching bookings excluding student:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while fetching bookings');
    }
  }
);

/**
 * Async thunk: fetchTeacherBookingsForStudent.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherBookingsForStudent = createAsyncThunk(
  "bookings/fetchTeacherBookingsForStudent",
  async ({ teacherId, studentId, startDate, endDate, status, isPast, sortBy, sortOrder }, { rejectWithValue }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (status) params.status = status;
      if (isPast !== undefined) params.isPast = isPast;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const response = await axios.get(`v1/bookings/teacher/${teacherId}/student/${studentId}`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching teacher bookings for student:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while fetching bookings');
    }
  }
);

// No-show reporting actions
/**
 * Async thunk: reportTeacherNoShow.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const reportTeacherNoShow = createAsyncThunk(
  "bookings/reportTeacherNoShow",
  async ({ bookingId, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`v1/bookings/${bookingId}/report-teacher-noshow`, { notes });
      return response.data;
    } catch (error) {
      console.error("Error reporting teacher no-show:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while reporting teacher no-show');
    }
  }
);

/**
 * Async thunk: reportStudentNoShow.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const reportStudentNoShow = createAsyncThunk(
  "bookings/reportStudentNoShow",
  async ({ bookingId, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`v1/bookings/${bookingId}/report-student-noshow`, { notes });
      return response.data;
    } catch (error) {
      console.error("Error reporting student no-show:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while reporting student no-show');
    }
  }
);

// Update join method for a booking
/**
 * Async thunk: updateJoinMethod.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateJoinMethod = createAsyncThunk(
  'bookings/updateJoinMethod',
  async ({ bookingId, method, userType }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`v1/bookings/${bookingId}/join-method`, {
        method,
        userType
      });
      return response.data;
    } catch (error) {
      console.error('Error updating join method:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || 'An error occurred while updating join method');
    }
  }
);
// Create teacher invite
/**
 * Async thunk: createInvite.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createInvite = createAsyncThunk(
  'bookings/createInvite',
  async ({ teacherId, startTime, endTime, fullName, email, phone }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/v1/admin/create-invite', {
        teacherId,
        startTime,
        endTime,
        fullName,
        email,
        phone
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating invite:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.message || 'Failed to create invite');
    }
  }
);
